import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

/**
 * Enter/Space open a clickable row from the keyboard — the same thing a
 * click on it does. `{ tabIndex, onKeyDown }` spreads straight onto the
 * `<tr className="row-clickable">`.
 *
 * Scoped to `e.target === e.currentTarget`: a button or input inside the row
 * (the col-action cell's Remove, a chip toggle) is its own target when it has
 * focus, and Space on it has to type or activate in place — answering that
 * keystroke here too would open the row behind whatever was just pressed.
 *
 * A free function, in its own file rather than in `ui.tsx` (which re-exports
 * it, the way `password-policy.ts` already does for `PASSWORD_MIN_LENGTH`):
 * there is no JSX in it, so `test/row-open.test.ts` can import it directly —
 * `ui.tsx` cannot be loaded by Node's own type-stripping, only bundled.
 */
export function rowOpen(onOpen: () => void): {
  tabIndex: 0;
  onKeyDown: (e: ReactKeyboardEvent<HTMLTableRowElement>) => void;
} {
  return {
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      onOpen();
    },
  };
}
